class CompleteTheLook extends HTMLElement {
  static COLOUR_LABEL = 'Colour';
  static WARMTH_LABEL = 'Warmth';
  static SIZE_LABEL = 'Size';

  connectedCallback() {
    this._form = this.dataset.formId
      ? document.getElementById(this.dataset.formId)
      : this.closest('form');

    this._state = new Map();
    this._primaryOptions = this._parsePrimaryOptions();
    this._primaryOptionNames = Object.keys(this._primaryOptions);
    this._primaryVariantPrice = 0;
    this._saleBadgeShow = this.dataset.saleBadgeShow === 'true';
    this._saleBadgeType = this.dataset.saleBadgeType || 'percent';
    this._percentLabelTemplate = this.dataset.percentLabel || '__AMOUNT__% off';
    this._valueLabelTemplate = this.dataset.valueLabel || '__AMOUNT__ off';

    this._initItems();
    this._bindPrimarySync();
    this._bindFormSubmit();
    this._bindCtaPriceSync();
    this._initPrimaryVariantPrice();
    this._updateCtaPrices();
  }

  _initPrimaryVariantPrice() {
    const variantInput = this._form?.querySelector('[name="id"]');
    if (!variantInput?.value) return;

    const productId = this._form?.dataset?.productId;
    const productJsonEl = productId
      ? document.getElementById(`ProductJson-${productId}`)
      : null;

    if (!productJsonEl) return;

    try {
      const product = JSON.parse(productJsonEl.textContent);
      const variant = product.variants?.find(v => String(v.id) === String(variantInput.value));
      if (variant?.price != null) this._primaryVariantPrice = variant.price;
    } catch (_) {}
  }

  _parsePrimaryOptions() {
    try {
      return JSON.parse(this.dataset.primaryOptions || '{}');
    } catch (_) {
      return {};
    }
  }

  _getPrimaryValue(label) {
    return this._primaryOptions[label] || '';
  }

  _initItems() {
    this.querySelectorAll('.ctl__item').forEach(item => {
      const id = item.dataset.productId;
      let data = {};

      try {
        data = JSON.parse(item.querySelector('.ctl__data').textContent);
      } catch (_) {}

      const defaultSelection = this._findDefaultSelection(data);

      this._state.set(id, {
        el: item,
        data,
        stitchedProducts: data.stitchedProducts || [],
        activeProductId: defaultSelection.activeProductId,
        variants: defaultSelection.variants,
        selectedOptions: defaultSelection.selectedOptions,
        currentVariantId: defaultSelection.variantId,
        currentVariantPrice: defaultSelection.variantPrice,
        checked: false,
        userTouched: false,
      });

      this._refreshOptionUI(id);
      this._resolveVariant(id, false);

      const checkbox = item.querySelector('.ctl__check');
      if (checkbox) {
        checkbox.addEventListener('change', () => {
          const st = this._state.get(id);
          st.checked = checkbox.checked;
          if (!checkbox.checked) st.userTouched = false;

          const panel = item.querySelector('.ctl__panel');
          if (panel) {
            panel.hidden = !checkbox.checked;
            panel.setAttribute('aria-hidden', String(!checkbox.checked));
          }
          item.classList.toggle('is-checked', checkbox.checked);
          this._updateCtaPrices();
        });
      }

      item.querySelectorAll('.ctl__option[data-selector-type="dropdown"] .cc-select').forEach(ccSelect => {
        ccSelect.addEventListener('change', e => {
          const value = e.detail?.selectedValue;
          if (value === undefined) return;

          const st = this._state.get(id);
          st.userTouched = true;

          const group = ccSelect.closest('.ctl__option');
          const optIdx = parseInt(group.dataset.optionIndex, 10);
          const optionName = group.dataset.optionName;

          if (group?.classList.contains('option-selector--swatch')) {
            ccSelect.querySelector('.cc-select__btn')?.setAttribute('aria-label', value);
          }

          if (st.stitchedProducts.length && optionName === CompleteTheLook.COLOUR_LABEL) {
            const stitched = st.stitchedProducts.find(
              sp => sp.colour && sp.colour.toLowerCase() === value.toLowerCase()
            );
            if (stitched) {
              this._setActiveProduct(id, stitched.id, value, optIdx);
              return;
            }
          }

          if (st.stitchedProducts.length && optionName === CompleteTheLook.WARMTH_LABEL) {
            const stitched = st.stitchedProducts.find(
              sp => sp.warmth && sp.warmth.toLowerCase() === value.toLowerCase()
            );
            if (stitched) {
              this._setActiveProduct(id, stitched.id, value, optIdx);
              return;
            }
          }

          st.selectedOptions[optIdx] = value;
          this._resolveVariant(id);
        });
      });
    });
  }

  _findDefaultSelection(data) {
    const {
      productId,
      variants = [],
      options = [],
      stitchedProducts = [],
    } = data;

    const primaryColour = this._getPrimaryValue(CompleteTheLook.COLOUR_LABEL);
    const primaryWarmth = this._getPrimaryValue(CompleteTheLook.WARMTH_LABEL);
    const primarySize = this._getPrimaryValue(CompleteTheLook.SIZE_LABEL);

    const colourIdx = options.findIndex(o => o.name === CompleteTheLook.COLOUR_LABEL);
    const warmthIdx = options.findIndex(o => o.name === CompleteTheLook.WARMTH_LABEL);
    const sizeIdx = options.findIndex(o => o.name === CompleteTheLook.SIZE_LABEL);

    let activeProduct = null;
    let activeVariants = variants;
    let activeProductId = productId;

    if (stitchedProducts.length) {
      if (colourIdx >= 0 && primaryColour) {
        activeProduct = stitchedProducts.find(
          sp => sp.colour && sp.colour.toLowerCase() === primaryColour.toLowerCase()
        );
      } else if (warmthIdx >= 0 && primaryWarmth) {
        activeProduct = stitchedProducts.find(
          sp => sp.warmth && sp.warmth.toLowerCase() === primaryWarmth.toLowerCase()
        );
      }

      if (!activeProduct) {
        activeProduct =
          stitchedProducts.find(sp => sp.id === productId) || stitchedProducts[0];
      }

      if (activeProduct) {
        activeProductId = activeProduct.id;
        activeVariants = activeProduct.variants || variants;
      }
    }

    const selectedOptions = options.map((opt, idx) => {
      if (idx === colourIdx && activeProduct?.colour) return activeProduct.colour;
      if (idx === warmthIdx && activeProduct?.warmth) return activeProduct.warmth;
      if (idx === sizeIdx && primarySize) return primarySize;
      if (opt.name === CompleteTheLook.COLOUR_LABEL && primaryColour) return primaryColour;
      if (opt.name === CompleteTheLook.WARMTH_LABEL && primaryWarmth) return primaryWarmth;
      if (opt.name === CompleteTheLook.SIZE_LABEL && primarySize) return primarySize;
      return opt.values?.[0] || '';
    });

    let variant =
      this._findVariant(activeVariants, selectedOptions) ||
      this._findVariantBySize(activeVariants, selectedOptions, sizeIdx, primarySize) ||
      activeVariants.find(v => v.available) ||
      activeVariants[0] ||
      null;

    if (variant) {
      return {
        activeProductId,
        variants: activeVariants,
        selectedOptions: [...variant.options],
        variantId: variant.id,
        variantPrice: variant.price || 0,
      };
    }

    return {
      activeProductId,
      variants: activeVariants,
      selectedOptions,
      variantId: null,
      variantPrice: 0,
    };
  }

  _findVariant(variants, selectedOptions) {
    return variants.find(v =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  _findVariantBySize(variants, selectedOptions, sizeIdx, primarySize) {
    if (sizeIdx < 0 || !primarySize) return null;

    const sizeOptions = [...selectedOptions];
    sizeOptions[sizeIdx] = primarySize;

    return (
      this._findVariant(variants, sizeOptions) ||
      variants.find(v => v.available && v.options[sizeIdx] === primarySize) ||
      variants.find(v => v.options[sizeIdx] === primarySize) ||
      null
    );
  }

  _setActiveProduct(productId, stitchedProductId, optionValue, optIdx) {
    const st = this._state.get(productId);
    const stitched = st.stitchedProducts.find(sp => sp.id === stitchedProductId);
    if (!stitched) return;

    const preservedSize =
      st.selectedOptions[
        st.data.options.findIndex(o => o.name === CompleteTheLook.SIZE_LABEL)
      ];

    st.activeProductId = stitchedProductId;
    st.variants = stitched.variants || st.data.variants;
    st.selectedOptions[optIdx] = optionValue;

    const sizeIdx = st.data.options.findIndex(o => o.name === CompleteTheLook.SIZE_LABEL);
    if (sizeIdx >= 0 && preservedSize) {
      st.selectedOptions[sizeIdx] = preservedSize;
    }

    this._resolveVariant(productId);
  }

  _applyPrimaryDefaults(primaryOptions) {
    if (primaryOptions && typeof primaryOptions === 'object' && !Array.isArray(primaryOptions)) {
      this._primaryOptions = primaryOptions;
    }

    this._state.forEach((st, id) => {
      if (st.userTouched) return;

      const defaultSelection = this._findDefaultSelection(st.data);
      st.activeProductId = defaultSelection.activeProductId;
      st.variants = defaultSelection.variants;
      st.selectedOptions = defaultSelection.selectedOptions;
      st.currentVariantId = defaultSelection.variantId;
      st.currentVariantPrice = defaultSelection.variantPrice;

      this._refreshOptionUI(id);
      this._resolveVariant(id, false);
    });
  }

  _bindPrimarySync() {
    const variantInput = this._form?.querySelector('[name="id"]');
    if (!variantInput) return;

    variantInput.addEventListener('change', e => {
      if (!e.detail?.selectedOptions) return;

      const updated = { ...this._primaryOptions };
      this._primaryOptionNames.forEach((name, i) => {
        if (e.detail.selectedOptions[i] !== undefined) {
          updated[name] = e.detail.selectedOptions[i];
        }
      });

      this._applyPrimaryDefaults(updated);
    });
  }

  _refreshOptionUI(productId) {
    const st = this._state.get(productId);
    if (!st) return;

    st.el.querySelectorAll('.ctl__option').forEach(group => {
      const idx = parseInt(group.dataset.optionIndex, 10);
      const val = st.selectedOptions[idx];
      if (val === undefined) return;

      const ccSelect = group.querySelector('.cc-select');
      if (ccSelect) {
        ccSelect.dispatchEvent(new CustomEvent('selectOption', { detail: { value: val } }));
      }
    });
  }

  _updateItemPricing(productId) {
    const st = this._state.get(productId);
    if (!st) return;

    const pricingEl = st.el.querySelector('.ctl__pricing');
    if (!pricingEl) return;

    const priceEl = pricingEl.querySelector('.price');
    const currentEl = pricingEl.querySelector('.current-price');
    const wasEl = pricingEl.querySelector('.was-price');
    const badgeEl = pricingEl.querySelector('.ctl__sale-badge');
    const badgeTextEl = pricingEl.querySelector('.ctl__sale-badge-text');
    if (!priceEl || !currentEl) return;

    const match = this._findVariant(st.variants, st.selectedOptions);

    if (!match) {
      currentEl.textContent = '—';
      if (wasEl) wasEl.setAttribute('hidden', '');
      if (badgeEl) badgeEl.setAttribute('hidden', '');
      priceEl.classList.remove('on-sale');
      return;
    }

    currentEl.textContent = this._formatMoney(match.price);
    const onSale = match.compare_at_price > match.price;

    priceEl.classList.toggle('on-sale', onSale);

    if (wasEl) {
      if (onSale) {
        wasEl.textContent = this._formatMoney(match.compare_at_price);
        wasEl.removeAttribute('hidden');
      } else {
        wasEl.setAttribute('hidden', '');
      }
    }

    if (badgeEl && badgeTextEl) {
      if (onSale && this._saleBadgeShow) {
        if (this._saleBadgeType === 'percent') {
          const amount = Math.round((1 - match.price / match.compare_at_price) * 100);
          badgeTextEl.textContent = this._percentLabelTemplate.replace('__AMOUNT__', amount);
        } else {
          const amount = this._formatMoney(match.compare_at_price - match.price);
          badgeTextEl.textContent = this._valueLabelTemplate.replace('__AMOUNT__', amount);
        }
        badgeEl.removeAttribute('hidden');
      } else {
        badgeEl.setAttribute('hidden', '');
      }
    }
  }

  _resolveVariant(productId, refreshUI = true) {
    const st = this._state.get(productId);
    const match = this._findVariant(st.variants, st.selectedOptions);
    st.currentVariantId = match ? match.id : null;
    st.currentVariantPrice = match ? match.price || 0 : 0;

    const msg = st.el.querySelector('.ctl__unavailable');
    if (msg) msg.hidden = !!match;

    this._updateItemPricing(productId);

    if (refreshUI) this._refreshOptionUI(productId);
    if (st.checked) this._updateCtaPrices();
  }

  _bindCtaPriceSync() {
    document.addEventListener('product:variantChanged', e => {
      if (e.detail?.variant?.price != null) {
        this._primaryVariantPrice = e.detail.variant.price;
      }
      this._updateCtaPrices();
    });
  }

  _getCheckedAddonTotal() {
    return [...this._state.values()].reduce((total, st) => {
      if (!st.checked || !st.currentVariantId) return total;
      return total + (st.currentVariantPrice || 0);
    }, 0);
  }

  _formatMoney(cents) {
    const format =
      (typeof theme !== 'undefined' && theme.money_format) ||
      (window.Shopify && window.Shopify.money_format) ||
      '${{amount}}';

    if (typeof theme !== 'undefined' && theme.Shopify?.formatMoney) {
      return theme.Shopify.formatMoney(cents, format);
    }

    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, format);
    }

    return '$' + (cents / 100).toFixed(2);
  }

  _getCtaButtons() {
    if (!this._form) return [];

    const mainBtn = this._form.querySelector(
      '.quantity-submit-row__submit button[type="submit"]'
    );
    const stickyBtn = this._form.querySelector('.c-sticky-atc__button');

    return [mainBtn, stickyBtn].filter(Boolean);
  }

  _updateCtaPrices() {
    const buttons = this._getCtaButtons();
    if (!buttons.length) return;

    const totalCents = (this._primaryVariantPrice || 0) + this._getCheckedAddonTotal();
    const formatted = this._formatMoney(totalCents);

    buttons.forEach(btn => {
      const priceEl = btn.querySelector('[data-price]');
      if (priceEl) priceEl.textContent = formatted;
    });
  }

  _bindFormSubmit() {
    if (!this._form) return;

    this._form.addEventListener(
      'submit',
      e => {
        const checked = [...this._state.values()].filter(s => s.checked);
        if (!checked.length) return;

        e.preventDefault();
        e.stopPropagation();

        const fd = new FormData(this._form);
        const primaryId = parseInt(fd.get('id'), 10);
        const qty = parseInt(fd.get('quantity') || 1, 10);
        if (!primaryId) return;

        const properties = {};
        for (const [k, v] of fd.entries()) {
          const m = k.match(/^properties\[(.+)]$/);
          if (m) properties[m[1]] = v;
        }

        const primaryItem = { id: primaryId, quantity: qty };
        if (Object.keys(properties).length) primaryItem.properties = properties;

        const addonItems = checked
          .filter(s => s.currentVariantId)
          .map(s => ({ id: s.currentVariantId, quantity: 1 }));

        this._addToCart([primaryItem, ...addonItems]);
      },
      true
    );
  }

  _addToCart(items) {
    const btn = this._form?.querySelector('.quantity-submit-row__submit button[type="submit"]');
    const originalHTML = btn ? btn.innerHTML : null;

    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then(r => {
        if (!r.ok) return r.json().then(d => Promise.reject(d));
        return r.json();
      })
      .then(() => {
        if (btn) btn.innerHTML = btn.dataset.addedToCartText || 'Added to Cart';
        document.dispatchEvent(
          new CustomEvent('theme:cartchanged', { bubbles: true, detail: { openDrawer: true } })
        );
        setTimeout(() => {
          if (btn && originalHTML) {
            btn.disabled = false;
            btn.innerHTML = originalHTML;
            this._updateCtaPrices();
          }
        }, 2500);
      })
      .catch(() => {
        if (btn && originalHTML) {
          btn.disabled = false;
          btn.innerHTML = originalHTML;
        }
      });
  }
}

customElements.define('complete-the-look', CompleteTheLook);
